// Shrinks a photo in the browser before it's uploaded.
//
// A tablet photo is typically 3-5 MB. At 1600px on the long edge and 85%
// JPEG quality it lands around 300-500 KB — still sharp enough to read a
// cake inscription — which keeps uploads fast on a shop's wifi and keeps
// the server's disk from filling up.
//
// Falls back to the original file if anything goes wrong, so a stubborn
// image still gets through (the server enforces its own size cap).

const MAX_EDGE = 1600;
const QUALITY = 0.85;

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(fr.result);
  fr.onerror = () => reject(new Error('read failed'));
  fr.readAsDataURL(file);
});

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('decode failed'));
  img.src = src;
});

export async function fileToResizedDataUrl(file) {
  const original = await readAsDataUrl(file);
  try {
    const img = await loadImage(original);
    const { width, height } = img;
    if (!width || !height) return original;

    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
    // Already small enough and already a JPEG — leave it alone
    if (scale === 1 && file.type === 'image/jpeg') return original;

    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return original;
    // White backing, otherwise transparent PNGs come out black as JPEG
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const out = canvas.toDataURL('image/jpeg', QUALITY);
    // If the "optimised" version somehow got bigger, keep the original
    return out.length < original.length ? out : original;
  } catch (e) {
    return original;
  }
}

export const dataUrlBytes = (dataUrl) => {
  const i = (dataUrl || '').indexOf(',');
  if (i === -1) return 0;
  return Math.round((dataUrl.length - i - 1) * 0.75);
};

export const prettyBytes = (n) => {
  if (!n) return '0 KB';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// Where the <img> points. The access key is unguessable, which is what
// stands in for a login token here.
export const imageUrl = (accessKey) => `/api/images/${accessKey}`;

export const MAX_IMAGES = 5;
